<template>
  <div class="fixed inset-0 z-50 overflow-y-auto wizard-modal searchenginesubmission-wizard-modal">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity wizard-backdrop"></div>
      
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full wizard-dialog">
        <div class="bg-white px-6 pt-5 pb-4 wizard-body">
          <!-- Header -->
          <div class="flex items-center justify-between border-b border-gray-200 pb-4 mb-6">
            <div>
              <h3 class="text-lg font-medium text-gray-900">Submit to Search Engines</h3>
              <p class="text-sm text-gray-500 mt-1">Step {{ currentStep }} of {{ totalSteps }}: {{ stepTitles[currentStep - 1] }}</p>
            </div>
            <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Progress Bar -->
          <div class="mb-6">
            <div class="flex items-center">
              <div
                v-for="step in totalSteps"
                :key="step"
                class="flex items-center"
                :class="{ 'flex-1': step < totalSteps }"
              >
                <div
                  class="flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-medium"
                  :class="step <= currentStep 
                    ? 'bg-violet-600 border-violet-600 text-white' 
                    : 'border-gray-300 text-gray-500'"
                >
                  {{ step }}
                </div>
                <div
                  v-if="step < totalSteps"
                  class="flex-1 h-0.5 mx-4"
                  :class="step < currentStep ? 'bg-violet-600' : 'bg-gray-300'"
                ></div>
              </div>
            </div>
          </div>

          <!-- Step Content -->
          <div class="space-y-6">
            <!-- Step 1: Domain Configuration -->
            <div v-if="currentStep === 1">
              <h4 class="text-lg font-medium text-gray-900 mb-4">Domain Configuration</h4>
              
              <div class="space-y-4">
                <!-- Loading State -->
                <div v-if="loadingDomainStatus" class="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div class="flex items-center space-x-2">
                    <svg class="animate-spin h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span class="text-sm text-gray-600">Fetching live domain status from Cloudflare...</span>
                  </div>
                </div>

                <!-- Current Domain Display -->
                <div v-else class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div class="flex items-center justify-between mb-2">
                    <h5 class="text-sm font-medium text-blue-800">Current Website URL</h5>
                    <button
                      @click="fetchLiveDomainStatus"
                      class="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      Refresh
                    </button>
                  </div>
                  <p class="text-sm text-blue-700 font-mono mb-2">{{ getCurrentDomain() }}</p>
                  <p class="text-xs text-blue-600">{{ getDomainStatusText() }}</p>
                </div>

                <!-- Live Domain Details -->
                <div v-if="domainStatus?.cloudflare_enabled && domainStatus.domains.length > 0" class="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h5 class="text-sm font-medium text-gray-800 mb-2">Custom Domains Status</h5>
                  <div class="space-y-2">
                    <div
                      v-for="domain in domainStatus.domains"
                      :key="domain.name"
                      class="flex items-center justify-between text-xs"
                    >
                      <span class="font-mono">{{ domain.name }}</span>
                      <div class="flex items-center space-x-2">
                        <span 
                          :class="{
                            'bg-green-100 text-green-800': domain.status === 'active',
                            'bg-yellow-100 text-yellow-800': domain.status === 'pending',
                            'bg-red-100 text-red-800': domain.status === 'failed'
                          }"
                          class="px-2 py-1 rounded-full text-xs font-medium"
                        >
                          {{ domain.status }}
                        </span>
                        <span 
                          :class="{
                            'text-green-600': domain.ssl_status === 'active',
                            'text-yellow-600': domain.ssl_status === 'pending',
                            'text-red-600': domain.ssl_status === 'failed'
                          }"
                          class="text-xs"
                        >
                          SSL: {{ domain.ssl_status }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Custom Domain Recommendation -->
                <div v-if="!hasCustomDomain" class="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <svg class="h-5 w-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="ml-3">
                      <h5 class="text-sm font-medium text-amber-800">
                        Recommendation: Set up Custom Domain First
                      </h5>
                      <div class="mt-2 text-sm text-amber-700">
                        <p class="mb-2">For better SEO results, we recommend configuring your custom domain (like mycompany.com) before submitting to search engines.</p>
                        <ul class="list-disc list-inside space-y-1">
                          <li>Better brand authority and trust</li>
                          <li>Avoid URL changes that hurt SEO</li>
                          <li>Professional appearance in search results</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Success Message for Custom Domain -->
                <div v-else class="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <svg class="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="ml-3">
                      <h5 class="text-sm font-medium text-green-800">
                        Perfect! Custom Domain Configured
                      </h5>
                      <div class="mt-2 text-sm text-green-700">
                        Your custom domain is properly set up. This will provide the best SEO results and professional appearance.
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Action Options -->
                <div class="space-y-3">
                  <label class="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      v-model="domainChoice"
                      value="continue"
                      class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                    />
                    <div>
                      <div class="text-sm font-medium text-gray-900">Continue with current domain</div>
                      <div class="text-xs text-gray-500">Submit {{ getCurrentDomain() }} to search engines</div>
                    </div>
                  </label>
                  
                  <label v-if="!hasCustomDomain" class="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      v-model="domainChoice"
                      value="setup_domain"
                      class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                    />
                    <div>
                      <div class="text-sm font-medium text-gray-900">Set up custom domain first (Recommended)</div>
                      <div class="text-xs text-gray-500">Configure your domain before search engine submission</div>
                    </div>
                  </label>
                </div>

                <!-- Domain Setup Instructions -->
                <div v-if="domainChoice === 'setup_domain'" class="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h5 class="text-sm font-medium text-gray-900 mb-2">How to set up your custom domain:</h5>
                  <ol class="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                    <li>Purchase a domain from a registrar (GoDaddy, Namecheap, etc.)</li>
                    <li>In Cloudflare Pages, go to your site's custom domains section</li>
                    <li>Add your domain and follow the DNS configuration instructions</li>
                    <li>Wait for DNS propagation (usually 24-48 hours)</li>
                    <li>Return here to submit to search engines with your custom domain</li>
                  </ol>
                </div>
              </div>
            </div>

            <!-- Step 2: Search Engines Selection -->
            <div v-if="currentStep === 2">
              <h4 class="text-lg font-medium text-gray-900 mb-4">Select Search Engines</h4>
              <div class="space-y-3">
                <!-- IndexNow Protocol -->
                <label class="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="submissionData.searchEngines.indexnow"
                    class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <div class="flex items-center space-x-3 flex-1">
                    <div class="w-6 h-6 bg-purple-500 rounded flex items-center justify-center">
                      <span class="text-white text-xs font-bold">I</span>
                    </div>
                    <div class="flex-1">
                      <div class="flex items-center space-x-2">
                        <div class="text-sm font-medium text-gray-900">IndexNow Protocol</div>
                        <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      </div>
                      <div class="text-xs text-gray-500">Real-time indexing for Bing, Yandex, Seznam.cz, and Naver</div>
                      <div class="text-xs text-purple-600 mt-1">✓ Instant notifications • ✓ No API keys required</div>
                    </div>
                  </div>
                </label>

                <!-- Google Search Console -->
                <label class="flex items-start space-x-3" :class="{ 'cursor-not-allowed opacity-60': !searchEngineStatus?.google?.enabled }">
                  <input
                    type="checkbox"
                    v-model="submissionData.searchEngines.google"
                    :disabled="!searchEngineStatus?.google?.enabled"
                    class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <div class="flex items-center space-x-3 flex-1">
                    <div class="w-6 h-6 bg-blue-500 rounded flex items-center justify-center">
                      <span class="text-white text-xs font-bold">G</span>
                    </div>
                    <div class="flex-1">
                      <div class="flex items-center space-x-2">
                        <div class="text-sm font-medium text-gray-900">Google Search Console</div>
                        <span v-if="googleOAuthConnected" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Connected
                        </span>
                        <span v-else class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Connect Account
                        </span>
                      </div>
                      <div class="text-xs text-gray-500">Submit to Google for indexing and search visibility</div>
                      <div class="text-xs text-blue-600 mt-1">✓ URL indexing • ✓ Sitemap submission • ✓ Real-time notifications</div>
                      <button
                        v-if="!googleOAuthConnected"
                        @click="initiateGoogleOAuth"
                        :disabled="loadingGoogleAuth"
                        class="mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {{ loadingGoogleAuth ? 'Connecting...' : 'Connect with Google' }}
                      </button>
                      <button
                        v-else
                        @click="disconnectGoogleOAuth"
                        :disabled="loadingGoogleAuth"
                        class="mt-2 text-xs bg-gray-400 text-white px-3 py-1 rounded hover:bg-gray-500 disabled:opacity-50"
                      >
                        {{ loadingGoogleAuth ? 'Disconnecting...' : 'Disconnect' }}
                      </button>
                    </div>
                  </div>
                </label>
                <!-- Bing Webmaster Tools -->
                <label class="flex items-start space-x-3" :class="{ 'cursor-not-allowed opacity-60': !searchEngineStatus?.bing?.enabled }">
                  <input
                    type="checkbox"
                    v-model="submissionData.searchEngines.bing"
                    :disabled="!searchEngineStatus?.bing?.enabled"
                    class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                  />
                  <div class="flex items-center space-x-3 flex-1">
                    <div class="w-6 h-6 bg-green-600 rounded flex items-center justify-center">
                      <span class="text-white text-xs font-bold">B</span>
                    </div>
                    <div class="flex-1">
                      <div class="flex items-center space-x-2">
                        <div class="text-sm font-medium text-gray-900">Bing Webmaster Tools</div>
                        <span v-if="searchEngineStatus?.bing?.enabled" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Available
                        </span>
                        <span v-else class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          Coming Soon
                        </span>
                      </div>
                      <div class="text-xs text-gray-500">Submit to Microsoft Bing and Yahoo search</div>
                      <div class="text-xs text-green-600 mt-1">✓ Direct URL submission • ✓ Sitemap management • ✓ Crawl statistics</div>
                    </div>
                  </div>
                </label>
                
                <!-- Note about other search engines -->
                <div class="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <h6 class="text-xs font-medium text-blue-800 mb-1">Additional Coverage</h6>
                  <p class="text-xs text-blue-700">
                    IndexNow protocol also notifies: <strong>Yandex, Seznam.cz, and Naver</strong> automatically.
                    Other search engines (DuckDuckGo, Baidu) will discover your site through sitemaps and backlinks.
                  </p>
                </div>
              </div>
            </div>

            <!-- Step 3: Sitemap & Technical Setup -->
            <div v-if="currentStep === 3">
              <h4 class="text-lg font-medium text-gray-900 mb-4">Sitemap & Technical Setup</h4>
              <div class="space-y-4">
                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="ml-3">
                      <h5 class="text-sm font-medium text-blue-800">
                        Automatic Setup
                      </h5>
                      <div class="mt-2 text-sm text-blue-700">
                        We'll automatically generate and configure the necessary files for search engine submission.
                      </div>
                    </div>
                  </div>
                </div>

                <div class="space-y-3">
                  <label class="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="submissionData.sitemapOptions.generateSitemap"
                      class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                      checked
                      disabled
                    />
                    <div>
                      <div class="text-sm font-medium text-gray-900">Generate XML Sitemap</div>
                      <div class="text-xs text-gray-500">Create sitemap.xml with all pages for search engines</div>
                    </div>
                  </label>
                  <label class="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="submissionData.sitemapOptions.robotsTxt"
                      class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                    />
                    <div>
                      <div class="text-sm font-medium text-gray-900">Create/Update robots.txt</div>
                      <div class="text-xs text-gray-500">Configure crawling rules and sitemap location</div>
                    </div>
                  </label>
                  <label class="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="submissionData.sitemapOptions.indexNow"
                      class="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                    />
                    <div>
                      <div class="text-sm font-medium text-gray-900">Enable IndexNow Protocol</div>
                      <div class="text-xs text-gray-500">Instantly notify search engines of content changes</div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <!-- Step 4: Pages Selection -->
            <div v-if="currentStep === 4">
              <h4 class="text-lg font-medium text-gray-900 mb-4">Select Pages to Submit</h4>
              <div class="space-y-4">
                <div>
                  <label class="flex items-center space-x-3 cursor-pointer mb-3">
                    <input
                      type="radio"
                      v-model="submissionData.pageSelection"
                      value="all"
                      class="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                    />
                    <div>
                      <div class="text-sm font-medium text-gray-900">Submit All Pages</div>
                      <div class="text-xs text-gray-500">Include all discoverable pages on the website</div>
                    </div>
                  </label>
                  <label class="flex items-center space-x-3 cursor-pointer mb-3">
                    <input
                      type="radio"
                      v-model="submissionData.pageSelection"
                      value="priority"
                      class="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                    />
                    <div>
                      <div class="text-sm font-medium text-gray-900">Priority Pages Only</div>
                      <div class="text-xs text-gray-500">Submit main pages first for faster indexing</div>
                    </div>
                  </label>
                  <label class="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      v-model="submissionData.pageSelection"
                      value="custom"
                      class="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                    />
                    <div>
                      <div class="text-sm font-medium text-gray-900">Custom Selection</div>
                      <div class="text-xs text-gray-500">Specify which pages to submit</div>
                    </div>
                  </label>
                </div>

                <div v-if="submissionData.pageSelection === 'priority'" class="bg-gray-50 rounded-lg p-4">
                  <h5 class="text-sm font-medium text-gray-900 mb-2">Priority pages typically include:</h5>
                  <ul class="text-xs text-gray-600 space-y-1">
                    <li>• Homepage</li>
                    <li>• About page</li>
                    <li>• Services/Products pages</li>
                    <li>• Contact page</li>
                    <li>• Recent blog posts</li>
                  </ul>
                </div>

                <div v-if="submissionData.pageSelection === 'custom'" class="space-y-3">
                  <div>
                    <label for="customPages" class="block text-sm font-medium text-gray-700 mb-1">
                      Specific Pages to Submit
                    </label>
                    <textarea
                      id="customPages"
                      v-model="submissionData.customPages"
                      rows="4"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      placeholder="/
/about
/services
/contact
/blog/latest-post"
                    ></textarea>
                    <p class="text-xs text-gray-500 mt-1">Enter page URLs, one per line (relative paths)</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 5: Review & Submit -->
            <div v-if="currentStep === 5">
              <h4 class="text-lg font-medium text-gray-900 mb-4">Review & Submit</h4>
              <div class="space-y-4">
                <div class="bg-gray-50 rounded-lg p-4">
                  <h5 class="text-sm font-medium text-gray-900 mb-3">Submission Summary</h5>
                  <div class="text-xs text-gray-600 space-y-2">
                    <div><strong>Search Engines:</strong> {{ selectedSearchEnginesText }}</div>
                    <div><strong>Pages:</strong> {{ pageSelectionText }}</div>
                    <div><strong>Technical Setup:</strong> {{ technicalSetupText }}</div>
                  </div>
                </div>

                <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <svg class="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="ml-3">
                      <h5 class="text-sm font-medium text-yellow-800">Important Notes</h5>
                      <div class="mt-2 text-sm text-yellow-700">
                        <ul class="list-disc list-inside space-y-1">
                          <li>Indexing typically takes 1-7 days for new content</li>
                          <li>Some search engines may require manual verification</li>
                          <li>Regular submissions help maintain search visibility</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Step 6: Submission Results -->
            <div v-if="currentStep === 6 && submissionResults">
              <h4 class="text-lg font-medium text-gray-900 mb-4">Submission Results</h4>
              
              <!-- Success Message -->
              <div class="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div class="flex">
                  <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                  </div>
                  <div class="ml-3">
                    <h5 class="text-sm font-medium text-green-800">Submission Successful!</h5>
                    <p class="text-sm text-green-700 mt-1">
                      Your website has been successfully submitted to selected search engines.
                    </p>
                  </div>
                </div>
              </div>
              
              <!-- Results Details -->
              <div class="space-y-4">
                <!-- Domain Info -->
                <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h6 class="text-sm font-medium text-gray-900 mb-2">Submitted Domain</h6>
                  <p class="text-lg font-mono text-blue-600">{{ submissionResults.domain }}</p>
                  <p class="text-xs text-gray-500 mt-1">{{ submissionResults.submittedUrls?.length || 0 }} URLs submitted</p>
                </div>
                
                <!-- IndexNow Results -->
                <div v-if="submissionResults.submissionResults?.indexnow" class="bg-white border border-gray-200 rounded-lg p-4">
                  <div class="flex items-center justify-between mb-2">
                    <div class="flex items-center space-x-2">
                      <div class="w-6 h-6 bg-purple-500 rounded flex items-center justify-center">
                        <span class="text-white text-xs font-bold">I</span>
                      </div>
                      <h6 class="text-sm font-medium text-gray-900">IndexNow Protocol</h6>
                    </div>
                    <span v-if="submissionResults.submissionResults.indexnow.success" 
                          class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      ✓ Success
                    </span>
                    <span v-else 
                          class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                      ✗ Failed
                    </span>
                  </div>
                  
                  <div v-if="submissionResults.submissionResults.indexnow.success" class="text-sm text-gray-600">
                    <p>Status: {{ submissionResults.submissionResults.indexnow.statusText }} ({{ submissionResults.submissionResults.indexnow.status }})</p>
                    <p class="text-xs text-purple-600 mt-1">
                      Notified: <strong>Bing, Yandex, Seznam.cz, and Naver</strong>
                    </p>
                    <p class="text-xs text-gray-500">{{ new Date(submissionResults.submissionResults.indexnow.timestamp).toLocaleString() }}</p>
                  </div>
                  
                  <div v-else class="text-sm text-red-600">
                    Error: {{ submissionResults.submissionResults.indexnow.error || 'Unknown error' }}
                  </div>
                </div>
                
                <!-- Google Results (if attempted) -->
                <div v-if="submissionResults.submissionResults?.google" class="bg-white border border-gray-200 rounded-lg p-4 opacity-60">
                  <div class="flex items-center justify-between mb-2">
                    <div class="flex items-center space-x-2">
                      <div class="w-6 h-6 bg-blue-500 rounded flex items-center justify-center">
                        <span class="text-white text-xs font-bold">G</span>
                      </div>
                      <h6 class="text-sm font-medium text-gray-900">Google Search Console</h6>
                    </div>
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                      Not Available
                    </span>
                  </div>
                  <p class="text-sm text-gray-500">{{ submissionResults.submissionResults.google.message }}</p>
                </div>
                
                <!-- Bing Results (if attempted) -->
                <div v-if="submissionResults.submissionResults?.bing" class="bg-white border border-gray-200 rounded-lg p-4 opacity-60">
                  <div class="flex items-center justify-between mb-2">
                    <div class="flex items-center space-x-2">
                      <div class="w-6 h-6 bg-green-600 rounded flex items-center justify-center">
                        <span class="text-white text-xs font-bold">B</span>
                      </div>
                      <h6 class="text-sm font-medium text-gray-900">Bing Webmaster Tools</h6>
                    </div>
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                      Not Available
                    </span>
                  </div>
                  <p class="text-sm text-gray-500">{{ submissionResults.submissionResults.bing.message }}</p>
                </div>
                
                <!-- Next Steps -->
                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h6 class="text-sm font-medium text-blue-800 mb-2">What Happens Next?</h6>
                  <ul class="text-sm text-blue-700 space-y-1 list-disc list-inside">
                    <li>Search engines will begin crawling your website within 24-48 hours</li>
                    <li>IndexNow protocol provides instant notifications for future content updates</li>
                    <li>You can monitor indexing progress through each search engine's webmaster tools</li>
                    <li>Regular content updates will automatically notify search engines via IndexNow</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="bg-gray-50 px-6 py-3 flex items-center justify-between">
          <button
            v-if="currentStep > 1 && currentStep < 6"
            @click="previousStep"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <div v-else></div>

          <div class="space-x-3">
            <button
              @click="$emit('close')"
              class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              {{ currentStep === 6 ? 'Close' : 'Cancel' }}
            </button>
            <button
              v-if="currentStep < 5"
              @click="nextStep"
              :disabled="!canProceed"
              class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              v-else-if="currentStep === 5"
              @click="submitToSearchEngines"
              :disabled="!canProceed || isSubmitting"
              class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="isSubmitting" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
              {{ isSubmitting ? 'Submitting...' : 'Submit to Search Engines' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, reactive, onMounted } from 'vue'
import api from '../utils/api'

export default {
  name: 'SearchEngineSubmissionModal',
  props: {
    site: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'create'],
  setup(props, { emit }) {
    const currentStep = ref(1)
    const totalSteps = 6
    const isSubmitting = ref(false)
    
    const stepTitles = [
      'Domain Configuration',
      'Search Engines',
      'Technical Setup', 
      'Pages Selection',
      'Review & Submit',
      'Results'
    ]

    const domainChoice = ref('continue')
    const domainStatus = ref(null)
    const loadingDomainStatus = ref(false)
    const searchEngineStatus = ref(null)
    const submissionResults = ref(null)
    
    const submissionData = reactive({
      searchEngines: {
        indexnow: true,
        google: false,
        bing: false
      },
      sitemapOptions: {
        generateSitemap: true,
        robotsTxt: true,
        indexNow: false
      },
      pageSelection: 'all', // 'all', 'priority', 'custom'
      customPages: ''
    })

    const hasCustomDomain = computed(() => {
      if (domainStatus.value?.cloudflare_enabled) {
        return domainStatus.value.hasCustomDomain
      }
      return props.site?.custom_domain && !props.site.custom_domain.includes('.pages.dev')
    })

    const getCurrentDomain = () => {
      if (domainStatus.value?.cloudflare_enabled) {
        return domainStatus.value.current_domain
      }
      if (props.site?.custom_domain) return props.site.custom_domain
      if (props.site?.preview_url) return props.site.preview_url.replace('-preview', '')
      return `https://${props.site?.subdomain}.pages.dev`
    }

    const getDomainStatusText = () => {
      if (!domainStatus.value?.cloudflare_enabled) {
        return 'Using database values (Cloudflare not enabled)'
      }
      
      const { domains, activeDomains, pendingDomains } = domainStatus.value
      if (activeDomains.length > 0) {
        return `${activeDomains.length} active custom domain(s)`
      }
      if (pendingDomains.length > 0) {
        return `${pendingDomains.length} pending domain(s)`
      }
      if (domains.length > 0) {
        return 'Custom domains configured but not active'
      }
      return 'No custom domains configured'
    }

    const fetchLiveDomainStatus = async () => {
      if (!props.site?.id) return
      
      loadingDomainStatus.value = true
      try {
        // Use production domains endpoint for SEO submission (filters out preview domains)
        const response = await api.get(`/sites/${props.site.id}/domains/production`)
        domainStatus.value = response.data.domain_status
      } catch (error) {
        console.error('Failed to fetch production domain status:', error)
        // Fallback to props-based detection
      } finally {
        loadingDomainStatus.value = false
      }
    }

    const selectedSearchEnginesText = computed(() => {
      const engines = []
      if (submissionData.searchEngines.google) engines.push('Google')
      if (submissionData.searchEngines.bing) engines.push('Bing')
      if (submissionData.searchEngines.yandex) engines.push('Yandex')
      if (submissionData.searchEngines.duckduckgo) engines.push('DuckDuckGo')
      if (submissionData.searchEngines.baidu) engines.push('Baidu')
      return engines.length > 0 ? engines.join(', ') : 'None selected'
    })

    const pageSelectionText = computed(() => {
      switch (submissionData.pageSelection) {
        case 'all': return 'All website pages'
        case 'priority': return 'Priority pages only'
        case 'custom': return 'Custom page selection'
        default: return 'Not selected'
      }
    })

    const technicalSetupText = computed(() => {
      const options = []
      if (submissionData.sitemapOptions.generateSitemap) options.push('XML Sitemap')
      if (submissionData.sitemapOptions.robotsTxt) options.push('robots.txt')
      if (submissionData.sitemapOptions.indexNow) options.push('IndexNow')
      return options.length > 0 ? options.join(', ') : 'Basic setup'
    })

    const canProceed = computed(() => {
      switch (currentStep.value) {
        case 1:
          return domainChoice.value !== ''
        case 2:
          return Object.values(submissionData.searchEngines).some(engine => engine)
        case 3:
          return true // Technical setup is optional
        case 4:
          if (submissionData.pageSelection === 'custom') {
            return submissionData.customPages.trim() !== ''
          }
          return submissionData.pageSelection !== ''
        case 5:
          return true
        default:
          return false
      }
    })

    const nextStep = () => {
      if (currentStep.value === 1 && domainChoice.value === 'setup_domain') {
        // Close modal if user wants to setup domain first
        emit('close')
        return
      }
      
      if (currentStep.value < totalSteps && canProceed.value) {
        currentStep.value++
      }
    }

    const previousStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--
      }
    }

    // Fetch search engine status
    const fetchSearchEngineStatus = async () => {
      if (!props.site?.id) return
      
      try {
        const response = await api.get(`/sites/${props.site.id}/search-engines/status`)
        searchEngineStatus.value = response.data.search_engine_status
      } catch (error) {
        console.error('Failed to fetch search engine status:', error)
      }
    }

    const submitToSearchEngines = async () => {
      if (!canProceed.value) return
      
      isSubmitting.value = true
      
      try {
        // Prepare selected engines array
        const selectedEngines = []
        if (submissionData.searchEngines.indexnow) selectedEngines.push('indexnow')
        if (submissionData.searchEngines.google) selectedEngines.push('google')
        if (submissionData.searchEngines.bing) selectedEngines.push('bing')

        // Prepare selected pages array for custom selection
        const selectedPages = submissionData.pageSelection === 'custom' 
          ? submissionData.customPages.split('\n').filter(page => page.trim())
          : []

        const payload = {
          selectedEngines,
          pageSelection: submissionData.pageSelection,
          technicalSetup: submissionData.sitemapOptions,
          selectedPages
        }

        const response = await api.post(`/sites/${props.site.id}/search-engines/submit`, payload)
        
        submissionResults.value = response.data.submission_result
        
        // Advance to results step
        currentStep.value = 6
        
      } catch (error) {
        console.error('Failed to submit to search engines:', error)
        // Handle error display  
      } finally {
        isSubmitting.value = false
      }
    }

    // For backward compatibility - generate AI prompt if needed
    const generateSubmissionPrompt = () => {
      // Generate the search engine submission prompt
      const targetDomain = getCurrentDomain()
      let prompt = `Submit this website to search engines and set up crawling infrastructure:\n\n`
      prompt += `**Target Domain:** ${targetDomain}\n\n`
      
      // Search engines
      prompt += `**Search Engines to Submit:**\n`
      const selectedEngines = []
      if (submissionData.searchEngines.google) selectedEngines.push('- Google Search Console (generate and submit sitemap)')
      if (submissionData.searchEngines.bing) selectedEngines.push('- Bing Webmaster Tools (submit sitemap and verify)')
      if (submissionData.searchEngines.yandex) selectedEngines.push('- Yandex Webmaster (submit for Russian market)')
      if (submissionData.searchEngines.duckduckgo) selectedEngines.push('- DuckDuckGo (privacy-focused submission)')
      if (submissionData.searchEngines.baidu) selectedEngines.push('- Baidu Webmaster Tools (for Chinese market)')
      prompt += selectedEngines.join('\n') + '\n\n'

      // Technical setup
      prompt += `**Technical Setup:**\n`
      prompt += `- Generate XML sitemap (sitemap.xml) with all discoverable pages\n`
      if (submissionData.sitemapOptions.robotsTxt) {
        prompt += `- Create/update robots.txt with proper crawling directives and sitemap location\n`
      }
      if (submissionData.sitemapOptions.indexNow) {
        prompt += `- Implement IndexNow protocol for instant content update notifications\n`
      }
      prompt += `- Add proper meta tags for search engine optimization\n`
      prompt += `- Implement structured data markup where appropriate\n\n`

      // Pages selection
      prompt += `**Pages to Include:**\n`
      switch (submissionData.pageSelection) {
        case 'all':
          prompt += `- All discoverable pages on the website\n`
          break
        case 'priority':
          prompt += `- Priority pages: Homepage, About, Services/Products, Contact, recent blog posts\n`
          break
        case 'custom':
          prompt += `- Specific pages: ${submissionData.customPages.split('\n').filter(page => page.trim()).join(', ')}\n`
          break
      }
      prompt += `\n`

      // Instructions
      prompt += `**Implementation Instructions:**\n`
      prompt += `- Ensure all pages have proper meta titles and descriptions\n`
      prompt += `- Verify internal linking structure is search-engine friendly\n`
      prompt += `- Check that all images have appropriate alt text\n`
      prompt += `- Optimize page loading speeds for better crawl budget\n`
      prompt += `- Set up proper canonical URLs to avoid duplicate content issues\n`
      prompt += `- Implement HTTPS if not already active\n\n`

      prompt += `**Verification & Monitoring:**\n`
      prompt += `- Set up webmaster tools accounts for monitoring\n`
      prompt += `- Configure search console for indexing status tracking\n`
      prompt += `- Enable crawl error notifications\n`
      prompt += `- Monitor search performance metrics\n\n`

      prompt += `Please implement these search engine submission and crawling optimizations to improve the website's discoverability and search engine visibility.`

      // Emit the create event with the generated prompt
      emit('create', { prompt, submissionData: { ...submissionData } })
    }

    // OAuth methods
    const googleOAuthConnected = ref(false)
    const loadingGoogleAuth = ref(false)

    const checkGoogleOAuthStatus = async () => {
      try {
        const response = await api.get('/oauth/google/status')
        googleOAuthConnected.value = response.data.connected && !response.data.isExpired
      } catch (error) {
        console.error('Failed to check Google OAuth status:', error)
        googleOAuthConnected.value = false
      }
    }

    const initiateGoogleOAuth = async () => {
      loadingGoogleAuth.value = true
      try {
        const response = await api.post('/oauth/google/login')
        if (response.data.authUrl) {
          // Redirect to Google OAuth login
          window.location.href = response.data.authUrl
        }
      } catch (error) {
        console.error('Failed to initiate Google OAuth:', error)
        alert('Failed to connect with Google. Please try again.')
      } finally {
        loadingGoogleAuth.value = false
      }
    }

    const disconnectGoogleOAuth = async () => {
      if (!confirm('Are you sure you want to disconnect your Google account?')) return

      loadingGoogleAuth.value = true
      try {
        await api.post('/oauth/google/disconnect')
        googleOAuthConnected.value = false
      } catch (error) {
        console.error('Failed to disconnect Google OAuth:', error)
        alert('Failed to disconnect. Please try again.')
      } finally {
        loadingGoogleAuth.value = false
      }
    }

    return {
      currentStep,
      totalSteps,
      stepTitles,
      domainChoice,
      domainStatus,
      loadingDomainStatus,
      searchEngineStatus,
      submissionResults,
      hasCustomDomain,
      getCurrentDomain,
      getDomainStatusText,
      fetchLiveDomainStatus,
      fetchSearchEngineStatus,
      submissionData,
      selectedSearchEnginesText,
      pageSelectionText,
      technicalSetupText,
      canProceed,
      isSubmitting,
      nextStep,
      previousStep,
      submitToSearchEngines,
      generateSubmissionPrompt,
      googleOAuthConnected,
      loadingGoogleAuth,
      checkGoogleOAuthStatus,
      initiateGoogleOAuth,
      disconnectGoogleOAuth
    }

    // Fetch live domain status, search engine status, and OAuth status when component mounts
    onMounted(() => {
      fetchLiveDomainStatus()
      fetchSearchEngineStatus()
      checkGoogleOAuthStatus()
    })
  }
}
</script>
<style src="./wizard-glass-modal.css"></style>

